import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(5px);
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    animation: fadeIn 0.3s ease;

    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }
`;

const CardContainer = styled.div`
    position: relative;
    width: 100%;
    max-width: 600px;
    background: #1a1a3e;
    border-radius: 20px;
    padding: 40px 30px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
    animation: scaleIn 0.3s ease;
    color: #f4d4a3;

    @keyframes scaleIn {
        from {
            transform: scale(0.9);
            opacity: 0;
        }
        to {
            transform: scale(1);
            opacity: 1;
        }
    }

    @media (max-width: 768px) {
        padding: 30px 20px;
        max-width: 90%;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 15px;
    right: 15px;
    background: transparent;
    border: none;
    color: #f4d4a3;
    font-size: 28px;
    cursor: pointer;
    width: 35px;
    height: 35px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.3s ease;
    opacity: 0.7;

    &:hover {
        opacity: 1;
        background: rgba(244, 212, 163, 0.1);
        transform: rotate(90deg);
    }
`;

const Title = styled.h2`
    font-size: 1.8rem;
    margin: 0 0 20px 0;
    text-align: center;
    color: #f4d4a3;
    font-weight: 600;

    @media (max-width: 768px) {
        font-size: 1.5rem;
    }
`;

const StickerPreview = styled.div`
    width: 100%;
    margin: 20px 0;
    border-radius: 12px;
    overflow: hidden;
    background: rgba(244, 212, 163, 0.05);
    border: 1px solid rgba(244, 212, 163, 0.2);
    min-height: 400px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;

    iframe {
        width: 100%;
        height: 600px;
        border: none;
        display: block;

        @media (max-width: 768px) {
            height: 500px;
        }
    }

    @media (max-width: 768px) {
        min-height: 350px;
    }
`;

const StickerGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    padding: 20px;
    width: 100%;
    max-height: 500px;
    overflow-y: auto;

    @media (max-width: 768px) {
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;
        padding: 15px;
        max-height: 400px;
    }

    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-track {
        background: rgba(244, 212, 163, 0.1);
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(244, 212, 163, 0.3);
        border-radius: 4px;

        &:hover {
            background: rgba(244, 212, 163, 0.5);
        }
    }
`;

const StickerItem = styled.div`
    aspect-ratio: 1;
    border-radius: 8px;
    overflow: hidden;
    background: rgba(244, 212, 163, 0.05);
    border: 1px solid rgba(244, 212, 163, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover {
        transform: scale(1.05);
        border-color: rgba(244, 212, 163, 0.3);
        box-shadow: 0 4px 12px rgba(244, 212, 163, 0.2);
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        padding: 4px;
    }
`;

const MainPreview = styled.div`
    width: 100%;
    padding: 30px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    position: relative;

    img {
        max-width: 100%;
        width: 100%;
        max-width: 400px;
        height: auto;
        border-radius: 16px;
        filter: drop-shadow(0 8px 24px rgba(244, 212, 163, 0.4));
        transition: all 0.3s ease;
        cursor: pointer;
        background: rgba(255, 255, 255, 0.05);
        padding: 20px;
        box-sizing: border-box;

        &:hover {
            transform: scale(1.02);
            filter: drop-shadow(0 12px 32px rgba(244, 212, 163, 0.5));
        }

        @media (max-width: 768px) {
            max-width: 100%;
            padding: 15px;
        }
    }
`;

const ViewAllButton = styled.button`
    background: linear-gradient(135deg, rgba(244, 212, 163, 0.15) 0%, rgba(244, 212, 163, 0.25) 100%);
    border: 2px solid rgba(244, 212, 163, 0.3);
    color: #f4d4a3;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-top: 10px;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover {
        background: linear-gradient(135deg, rgba(244, 212, 163, 0.25) 0%, rgba(244, 212, 163, 0.35) 100%);
        border-color: rgba(244, 212, 163, 0.5);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(244, 212, 163, 0.2);
    }

    &:active {
        transform: translateY(0);
    }
`;

const ActionButton = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 15px 30px;
    background: linear-gradient(135deg, #06c755 0%, #00a148 100%);
    color: white;
    text-decoration: none;
    border-radius: 8px;
    font-weight: 600;
    font-size: 1rem;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(6, 199, 85, 0.3);
    margin-top: 20px;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(6, 199, 85, 0.4);
        background: linear-gradient(135deg, #00a148 0%, #06c755 100%);
    }

    &:active {
        transform: translateY(0);
    }
`;

const Description = styled.p`
    text-align: center;
    color: #f4d4a3;
    opacity: 0.8;
    font-size: 0.95rem;
    margin: 20px 0;
    line-height: 1.6;
`;

const LineStickerCard = ({ onClose }) => {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language;
    const [showFallback, setShowFallback] = useState(true); // 預設顯示 fallback，因為 LINE Store 通常不允許 iframe
    const [viewMode, setViewMode] = useState('main'); // 'grid' 或 'main' - 預設顯示主圖
    const iframeRef = useRef(null);
    
    // LINE Store 產品 ID
    const productId = '18279399';
    
    // 生成貼圖列表 (01-16.png)
    const stickerImages = Array.from({ length: 16 }, (_, i) => {
        const num = String(i + 1).padStart(2, '0');
        return `/images/line-stickers/${num}.png`;
    });
    
    // 根據語言選擇對應的 LINE Store URL
    const getStoreUrl = () => {
        const langMap = {
            'zh-TW': 'zh-Hant',
            'ja': 'ja',
            'en': 'en'
        };
        const lang = langMap[currentLang] || 'en';
        return `https://store.line.me/stickershop/product/${productId}/${lang}`;
    };

    // 嘗試使用 LINE Store 的 iframe embed（通常會被阻止）
    const getEmbedUrl = () => {
        const langMap = {
            'zh-TW': 'zh-Hant',
            'ja': 'ja',
            'en': 'en'
        };
        const lang = langMap[currentLang] || 'en';
        return `https://store.line.me/stickershop/product/${productId}/${lang}`;
    };

    // 檢測 iframe 是否被阻止
    useEffect(() => {
        if (iframeRef.current && !showFallback) {
            const timer = setTimeout(() => {
                try {
                    // 嘗試訪問 iframe 內容，如果被阻止會拋出錯誤
                    const iframe = iframeRef.current;
                    if (iframe && iframe.contentWindow) {
                        // 如果能夠訪問，檢查是否真的載入了內容
                        try {
                            // 嘗試訪問 document 來檢測是否被跨域阻止
                            void iframe.contentWindow.document;
                        } catch (e) {
                            // 跨域錯誤，顯示 fallback
                            setShowFallback(true);
                        }
                    }
                } catch (err) {
                    // 被阻止，顯示 fallback
                    setShowFallback(true);
                }
            }, 2000); // 2秒後檢查

            return () => clearTimeout(timer);
        }
    }, [showFallback]);

    return (
        <Overlay onClick={onClose}>
            <CardContainer onClick={(e) => e.stopPropagation()}>
                <CloseButton onClick={onClose}>×</CloseButton>
                
                <Title>🐴 {t('home.lineSticker')}</Title>
                
                <Description>
                    {currentLang === 'zh-TW' && '可愛的馬與老虎貼圖組合，點擊下方按鈕前往 LINE Store 購買'}
                    {currentLang === 'ja' && 'かわいい馬とトラのスタンプセット。下のボタンをクリックしてLINEストアで購入'}
                    {currentLang === 'en' && 'Cute horse and tiger sticker set. Click the button below to purchase on LINE Store'}
                    {!['zh-TW', 'ja', 'en'].includes(currentLang) && 'Cute horse and tiger sticker set. Click the button below to purchase on LINE Store'}
                </Description>

                <StickerPreview>
                    {showFallback ? (
                        viewMode === 'main' ? (
                            <MainPreview>
                                <img 
                                    src="/images/line-stickers/main.png" 
                                    alt="LINE Sticker Main Preview"
                                    onClick={() => setViewMode('grid')}
                                />
                                <ViewAllButton onClick={() => setViewMode('grid')}>
                                    <span>👀</span>
                                    <span>
                                        {currentLang === 'zh-TW' && '查看所有貼圖 (16 張)'}
                                        {currentLang === 'ja' && 'すべてのスタンプを表示 (16枚)'}
                                        {currentLang === 'en' && 'View All Stickers (16)'}
                                        {!['zh-TW', 'ja', 'en'].includes(currentLang) && 'View All Stickers (16)'}
                                    </span>
                                </ViewAllButton>
                            </MainPreview>
                        ) : (
                            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 20px 10px 20px' }}>
                                    <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.8, fontWeight: 500 }}>
                                        {currentLang === 'zh-TW' && '📦 共 16 張貼圖'}
                                        {currentLang === 'ja' && '📦 全16枚のスタンプ'}
                                        {currentLang === 'en' && '📦 16 stickers total'}
                                        {!['zh-TW', 'ja', 'en'].includes(currentLang) && '📦 16 stickers total'}
                                    </p>
                                    <ViewAllButton
                                        onClick={() => setViewMode('main')}
                                        style={{ 
                                            padding: '8px 16px', 
                                            fontSize: '0.85rem',
                                            margin: 0
                                        }}
                                    >
                                        <span>🏠</span>
                                        <span>
                                            {currentLang === 'zh-TW' && '返回主圖'}
                                            {currentLang === 'ja' && 'メイン画像に戻る'}
                                            {currentLang === 'en' && 'Back to Main'}
                                            {!['zh-TW', 'ja', 'en'].includes(currentLang) && 'Back to Main'}
                                        </span>
                                    </ViewAllButton>
                                </div>
                                <StickerGrid>
                                    {stickerImages.map((src, index) => (
                                        <StickerItem key={index}>
                                            <img 
                                                src={src} 
                                                alt={`Sticker ${index + 1}`}
                                                loading="lazy"
                                            />
                                        </StickerItem>
                                    ))}
                                </StickerGrid>
                            </div>
                        )
                    ) : (
                        <iframe
                            ref={iframeRef}
                            src={getEmbedUrl()}
                            title="LINE Sticker Preview"
                            onError={() => setShowFallback(true)}
                            onLoad={() => {
                                // 延遲檢查是否真的載入成功
                                setTimeout(() => {
                                    try {
                                        if (iframeRef.current && iframeRef.current.contentWindow) {
                                            // 嘗試訪問 document 來檢測是否被跨域阻止
                                            void iframeRef.current.contentWindow.document;
                                        }
                                    } catch (err) {
                                        setShowFallback(true);
                                    }
                                }, 1000);
                            }}
                        />
                    )}
                </StickerPreview>

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <ActionButton 
                        href={getStoreUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {currentLang === 'zh-TW' && '前往 LINE Store 購買 →'}
                        {currentLang === 'ja' && 'LINEストアで購入 →'}
                        {currentLang === 'en' && 'Purchase on LINE Store →'}
                        {!['zh-TW', 'ja', 'en'].includes(currentLang) && 'Purchase on LINE Store →'}
                    </ActionButton>
                </div>
            </CardContainer>
        </Overlay>
    );
};

export default LineStickerCard;

